// src/pages/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { get } from "../../lib/http";
import { API } from "../../config/api";
import PaymentStatusWidget from "./payment/PaymentStatusWidget.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend
);

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/* --------------------------- Small UI bits ---------------------------- */
function SectionTitle({ children, right = null }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="font-sans text-base font-bold tracking-tight text-slate-900 sm:text-lg dark:text-white">
        {children}
      </h2>
      {right}
    </div>
  );
}

function ActionButton({ children, onClick, kind = "ghost", className = "", disabled = false }) {
  const cls =
    kind === "primary"
      ? "bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60"
      : "border hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-60";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-3 py-2 text-sm font-semibold ${cls} ${className}`}
    >
      {children}
    </button>
  );
}

/* ---------------------------- Main Component -------------------------- */
export default function AdminDashboard() {
  const [stats, setStats] = useState({
    revenue: 0,
    orders: 0,
    events: 0,
    attendees: 0,
    prev: { revenue: 0, orders: 0, events: 0, attendees: 0 },
  });
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d"); // 7d, 30d, 90d
  const [error, setError] = useState("");

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");

      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const headers = { Authorization: token ? `Bearer ${token}` : undefined };

      // --- Stats ---
      const statsResp = await get(`${API.dashboard.getDashboardStats}?range=${range}`, { headers });
      const statsData = statsResp?.data ?? statsResp;
      const incomingSummary = statsData?.summary;
      const summary = incomingSummary
        ? incomingSummary
        : {
            revenue: statsData?.totalRevenue ?? 0,
            orders: statsData?.totalBookings ?? 0,
            events: statsData?.totalEvents ?? 0,
            attendees:
              statsData?.totalAttendees ??
              (statsData?.totalBookings ? statsData.totalBookings * 3 : 0),
            prev: {
              revenue: Math.round((statsData?.totalRevenue || 0) * 0.9),
              orders: Math.max((statsData?.totalBookings || 0) - 10, 0),
              events: Math.max((statsData?.totalEvents || 0) - 2, 0),
              attendees: Math.max(
                (statsData?.totalAttendees ??
                  (statsData?.totalBookings ? statsData.totalBookings * 3 : 0)) - 50,
                0
              ),
            },
          };

      // --- Series ---
      const seriesResp = await get(`${API.dashboard.getDashboardSeries}?range=${range}`, { headers });
      const seriesData = seriesResp?.data ?? seriesResp;
      const series = {
        labels: Array.isArray(seriesData?.labels)
          ? seriesData.labels
          : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        revenue: Array.isArray(seriesData?.revenue) ? seriesData.revenue : [],
        orders: Array.isArray(seriesData?.orders) ? seriesData.orders : [],
        categories: Array.isArray(seriesData?.categories) ? seriesData.categories : [],
        topEvents: Array.isArray(seriesData?.topEvents) ? seriesData.topEvents : [],
        recentOrders: Array.isArray(seriesData?.recentOrders) ? seriesData.recentOrders : [],
      };

      setStats(summary);
      setSeries(series);
    } catch (e) {
      console.error("dashboard fetch error:", e);
      setError(e?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  /* -------------- charts (memoized) --------------- */
  const revenueLine = useMemo(() => {
    if (!series) return null;
    return {
      data: {
        labels: series.labels,
        datasets: [
          {
            label: "Revenue",
            data: series.revenue,
            borderColor: "rgba(245, 158, 11, 1)",
            backgroundColor: "rgba(245, 158, 11, 0.15)",
            fill: true,
            tension: 0.35,
            pointRadius: 2.5,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          y: { beginAtZero: true, grid: { color: "#E5E7EB" }, ticks: { color: "#475569" } },
          x: { grid: { display: false }, ticks: { color: "#475569" } },
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => `${INR(ctx.parsed.y)}` } },
        },
      },
    };
  }, [series]);

  const ordersBar = useMemo(() => {
    if (!series) return null;
    return {
      data: {
        labels: series.labels,
        datasets: [
          {
            label: "Orders",
            data: series.orders,
            backgroundColor: "rgba(16, 185, 129, 0.6)",
            borderColor: "rgba(16, 185, 129, 1)",
            borderWidth: 1,
            borderRadius: 6,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          y: { beginAtZero: true, grid: { color: "#E5E7EB" }, ticks: { color: "#475569" } },
          x: { grid: { display: false }, ticks: { color: "#475569" } },
        },
        plugins: { legend: { display: false } },
      },
    };
  }, [series]);

  const categoryDonut = useMemo(() => {
    if (!series) return null;
    return {
      data: {
        labels: series.categories.map((c) => c.name),
        datasets: [
          {
            data: series.categories.map((c) => c.value),
            backgroundColor: [
              "rgba(245, 158, 11, 0.8)",
              "rgba(99, 102, 241, 0.8)",
              "rgba(16, 185, 129, 0.8)",
              "rgba(239, 68, 68, 0.8)",
            ],
            borderWidth: 0,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: { position: "bottom", labels: { color: "#334155" } },
        },
        cutout: "62%",
      },
    };
  }, [series]);

  const topEventsRevenueBar = useMemo(() => {
    if (!series) return null;
    const top = [...(series.topEvents || [])].slice(0, 6);
    return {
      data: {
        labels: top.map((ev) => ev.name),
        datasets: [
          {
            label: "Revenue",
            data: top.map((ev) => Number(ev.revenue || 0)),
            backgroundColor: [
              "rgba(245, 158, 11, 0.82)",
              "rgba(16, 185, 129, 0.82)",
              "rgba(59, 130, 246, 0.82)",
              "rgba(239, 68, 68, 0.82)",
              "rgba(99, 102, 241, 0.82)",
              "rgba(20, 184, 166, 0.82)",
            ],
            borderRadius: 8,
            borderSkipped: false,
          },
        ],
      },
      options: {
        indexAxis: "y",
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: "#E5E7EB" },
            ticks: {
              color: "#475569",
              callback: (v) => INR(v),
            },
          },
          y: { grid: { display: false }, ticks: { color: "#475569" } },
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => INR(ctx.parsed.x || 0) } },
        },
      },
    };
  }, [series]);

  const exportCSV = () => {
    if (!series) return;
    const rows = [["Label", "Revenue", "Orders"]];
    series.labels.forEach((label, i) => {
      rows.push([label, series.revenue[i] || 0, series.orders[i] || 0]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dashboard-${range}.csv`;
    a.click();
  };

  /* ------------------------------ UI --------------------------------- */
  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <HeaderBar
          range={range}
          setRange={setRange}
          onRefresh={fetchAll}
          onExport={exportCSV}
          loading
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-xl border bg-white dark:bg-gray-800 dark:border-gray-700 p-5">
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-3 animate-pulse" />
              <div className="h-7 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-56 sm:h-64 rounded-xl border bg-white dark:bg-gray-800 dark:border-gray-700 animate-pulse" />
          <div className="h-56 sm:h-64 rounded-xl border bg-white dark:bg-gray-800 dark:border-gray-700 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <HeaderBar range={range} setRange={setRange} onRefresh={fetchAll} onExport={exportCSV} />
        <div className="rounded-xl border bg-white dark:bg-gray-800 dark:border-gray-700 p-6">
          <div className="text-rose-600 dark:text-rose-400 font-semibold">{error}</div>
          <div className="mt-3">
            <ActionButton kind="primary" onClick={fetchAll}>Retry</ActionButton>
          </div>
        </div>
      </div>
    );
  }

  const kpis = [
    { label: "Total revenue", value: INR(stats.revenue), chip: "₹" },
    { label: "Orders", value: stats.orders, chip: "#" },
    { label: "Active events", value: stats.events, chip: "◎" },
    { label: "Attendees", value: stats.attendees, chip: "◆" },
  ];

  return (
    <div className="space-y-6 p-1 font-sans sm:p-0">
      {/* Header */}
      <HeaderBar range={range} setRange={setRange} onRefresh={fetchAll} onExport={exportCSV} />

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition hover:border-amber-200/80 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-5"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-800/90 dark:text-amber-300/90 sm:text-xs">
                {k.label}
              </div>
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-amber-100 text-xs font-black text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                {k.chip}
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-[1.65rem]">
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <SectionTitle right={<span className="text-xs text-slate-500">Last {range.toUpperCase()}</span>}>
            Revenue Over Time
          </SectionTitle>
          <div className="h-56 sm:h-64">
            {revenueLine && revenueLine.data.datasets[0]?.data?.length > 0 ? (
              <Line data={revenueLine.data} options={revenueLine.options} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No revenue trend data in selected range.
              </div>
            )}
          </div>
        </div>

        {/* Payment Status Widget */}
        <PaymentStatusWidget />
      </div>

      {/* Orders chart + Category Split + tables */}
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <SectionTitle>Orders (Daily)</SectionTitle>
          <div className="h-56 sm:h-64">
            {ordersBar && ordersBar.data.datasets[0]?.data?.length > 0 ? (
              <Bar data={ordersBar.data} options={ordersBar.options} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No order trend data in selected range.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <SectionTitle>Category Split</SectionTitle>
          <div className="h-56 sm:h-64">
            {categoryDonut && categoryDonut.data.datasets[0]?.data?.length > 0 ? (
              <Doughnut data={categoryDonut.data} options={categoryDonut.options} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                Category distribution unavailable.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <SectionTitle>Top Events by Revenue</SectionTitle>
          <div className="h-56 sm:h-64">
            {topEventsRevenueBar && topEventsRevenueBar.data.labels.length > 0 ? (
              <Bar data={topEventsRevenueBar.data} options={topEventsRevenueBar.options} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No top event data available.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <SectionTitle>Top Events</SectionTitle>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2 hidden sm:table-cell">City</th>
                <th className="px-3 py-2 text-right">Tickets</th>
                <th className="px-3 py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {series.topEvents.map((ev) => (
                <tr key={ev.name} className="border-t">
                  <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                    {ev.name}
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell">{ev.city}</td>
                  <td className="px-3 py-2 text-right">{ev.tickets}</td>
                  <td className="px-3 py-2 text-right">{INR(ev.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent orders */}
      <div className="mt-6 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <SectionTitle>Recent Orders</SectionTitle>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2">Order ID</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2 hidden md:table-cell">Buyer</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Status</th>
                <th className="px-3 py-2 text-right hidden md:table-cell">Time</th>
              </tr>
            </thead>
            <tbody>
              {series.recentOrders.map((o) => (
                <tr key={o.id} className="border-t">
                  <td className="px-3 py-2 font-medium">{o.id}</td>
                  <td className="px-3 py-2">{o.event}</td>
                  <td className="px-3 py-2 hidden md:table-cell">{o.buyer}</td>
                  <td className="px-3 py-2 text-right">{INR(o.amount)}</td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        o.status === "Paid"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {o.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500 hidden md:table-cell">{o.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Header Bar --------------------------- */
function HeaderBar({ range, setRange, onRefresh, onExport, loading = false }) {
  return (
    <div className="mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Quick overview of sales and inventory
          </p>
        </div>

        {/* Actions: stack on mobile */}
        <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              disabled={loading}
              className="appearance-none rounded-lg border bg-white dark:bg-gray-900 px-3 py-2 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 dark:border-gray-700 w-full"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <svg
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <ActionButton onClick={onRefresh} className="w-full sm:w-auto" disabled={loading}>
            Refresh
          </ActionButton>
          <ActionButton kind="primary" onClick={onExport} className="w-full sm:w-auto" disabled={loading}>
            Export CSV
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
